import React, { useState } from "react";
import { Brain, Maximize2, Wallet } from "lucide-react";

const card = "rounded-2xl border border-[#00ff90]/20 bg-[#080808] shadow-[0_0_24px_#00ff9010]";

function fmt(n) {
  if (!n && n !== 0) return "—";
  if (n >= 1000000) return `£${(n/1000000).toFixed(2)}M`;
  if (n >= 1000)    return `£${(n/1000).toFixed(0)}k`;
  return `£${n}`;
}

// Currency symbols per payroll_country — keeps the location row's
// amount column compact and readable. Unknown country falls through
// to the £ default (UK-first product).
const CURRENCY_SYMBOL = {
  UK:   "£",   // £
  GB:   "£",
  IN:   "₹",   // ₹
  India:"₹",
  US:   "$",
  AE:   "AED ",
  UAE:  "AED ",
  EU:   "€",   // €
};

function fmtCountry(amount, country) {
  if (amount == null) return "—";
  const sym = CURRENCY_SYMBOL[country] || "£";
  if (amount >= 1_000_000) return `${sym}${(amount / 1_000_000).toFixed(2)}M`;
  if (amount >= 1_000)     return `${sym}${(amount / 1_000).toFixed(0)}k`;
  return `${sym}${Math.round(amount)}`;
}

export default function PayrollWidget({ data = {}, period, loading, onAskKira, onExpand, expanded }) {
  const [tab, setTab] = useState("overview");

  const depts  = data.by_department || [];
  const locs   = data.by_location   || [];
  const trend  = data.monthly_trend || [];
  const maxDeptCost = Math.max(...depts.map(d => d.gross || 0), 1);
  const maxLocCost  = Math.max(...locs.map(l => l.gross || 0), 1);

  return (
    <div className={`${card} flex flex-col`}>
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
        <div className="flex items-center gap-2">
          <Wallet size={14} className="text-[#60a5fa]/60" />
          <span className="text-sm font-semibold text-white/80">Payroll & Costs</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => onAskKira(data, "Analyse payroll costs across departments, identify cost drivers and efficiency opportunities, and forecast next quarter.")}
            className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-[#60a5fa]/20 bg-[#60a5fa]/5 text-[#60a5fa]/60 hover:bg-[#60a5fa]/10 hover:text-[#60a5fa] transition-colors">
            <Brain size={11} /> KIRA
          </button>
          {onExpand && <button onClick={onExpand} className="text-white/20 hover:text-white transition-colors">
            <Maximize2 size={13} />
          </button>}
        </div>
      </div>

      <div className="flex gap-1 px-4 pt-3">
        {["overview","departments","locations","trend","exceptions"].map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`text-xs px-3 py-1.5 rounded-lg transition-colors capitalize ${
              tab === t ? "bg-[#60a5fa]/10 text-[#60a5fa] border border-[#60a5fa]/20" : "text-white/30 hover:text-white/60"
            }`}>{t}</button>
        ))}
      </div>

      <div className="p-5 flex-1 space-y-4">
        {loading ? (
          <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-8 rounded-lg bg-white/5 animate-pulse" />)}</div>
        ) : (
          <>
          {tab === "overview" && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: "Monthly Cost",     value: fmt(data.monthly_cost),       color: "#60a5fa", delta: data.mom_change_pct },
                  { label: "Annual Run Rate",   value: fmt(data.annual_run_rate),    color: "#34d399", delta: null },
                  { label: "Cost Per Head",     value: fmt(data.cost_per_head),      color: "#fbbf24", delta: data.cost_per_head_delta },
                  { label: "Employer On-costs", value: fmt(data.employer_oncosts),   color: "#e879f9", delta: null },
                ].map(({ label, value, color, delta }) => (
                  <div key={label} className="rounded-xl border border-white/5 bg-white/3 p-3">
                    <p className="text-xs text-white/30 mb-1">{label}</p>
                    <p className="text-xl font-bold font-mono" style={{ color }}>{value}</p>
                    {delta != null && (
                      <p className={`text-xs mt-0.5 ${delta >= 0 ? "text-red-400/50" : "text-[#34d399]/50"}`}>
                        {delta >= 0 ? "+" : ""}{delta?.toFixed(1)}% vs last period
                      </p>
                    )}
                  </div>
                ))}
              </div>

              {/* Payroll breakdown waterfall */}
              {data.breakdown && (
                <div className="rounded-xl border border-white/5 bg-white/3 p-4">
                  <p className="text-xs text-white/30 uppercase tracking-wider mb-3">Cost Breakdown</p>
                  <div className="space-y-2">
                    {Object.entries(data.breakdown).map(([k, v]) => (
                      <div key={k} className="flex items-center gap-3">
                        <span className="text-xs text-white/40 w-32 truncate">{k}</span>
                        <div className="flex-1 h-1.5 rounded-full bg-white/5">
                          <div className="h-full rounded-full bg-[#60a5fa]/50"
                            style={{ width: `${Math.round((v/data.monthly_cost)*100)}%` }} />
                        </div>
                        <span className="text-xs font-mono text-white/40 w-16 text-right">{fmt(v)}</span>
                        <span className="text-xs text-white/20 w-10 text-right">{Math.round((v/data.monthly_cost)*100)}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {data.increment_cycle_cost != null && (
                <div className="rounded-xl border border-yellow-500/20 bg-yellow-500/5 p-3 flex items-center gap-3">
                  <span className="text-yellow-400 text-lg">⚠</span>
                  <div>
                    <p className="text-xs text-yellow-400/80 font-medium">Upcoming Increment Impact</p>
                    <p className="text-xs text-white/40 mt-0.5">
                      Estimated {fmt(data.increment_cycle_cost)} additional monthly cost
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === "departments" && (
            <div className="space-y-2">
              <div className="flex justify-between text-xs text-white/25 px-1 mb-3">
                <span>Department</span>
                <span>Cost / Head / On-costs</span>
              </div>
              {depts.length === 0 ? (
                <p className="text-white/20 text-sm italic text-center py-6">No department data.</p>
              ) : depts.map((d, i) => (
                <div key={i} className="rounded-xl border border-white/5 bg-white/3 px-4 py-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-white/70 font-medium">{d.dept}</span>
                    <div className="flex gap-4 text-xs font-mono">
                      <span className="text-[#60a5fa]/70">{fmt(d.gross)}</span>
                      <span className="text-white/30">{fmt(d.cost_per_head)}/head</span>
                    </div>
                  </div>
                  <div className="flex-1 h-1.5 rounded-full bg-white/5">
                    <div className="h-full rounded-full bg-[#60a5fa]/50"
                      style={{ width: `${Math.round((d.gross/maxDeptCost)*100)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "locations" && (
            <div className="space-y-2">
              <div className="flex justify-between text-xs text-white/25 px-1 mb-3">
                <span>Location</span>
                <span>Cost · Headcount</span>
              </div>
              {locs.length === 0 ? (
                <p className="text-white/20 text-sm italic text-center py-6">
                  No location data — current pay run has no location-tagged items.
                </p>
              ) : locs.map((l, i) => (
                <div key={i} className="rounded-xl border border-white/5 bg-white/3 px-4 py-3">
                  <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm text-white/70 font-medium truncate">{l.location}</span>
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-white/10 text-white/40">
                        {l.country || "UK"}
                      </span>
                    </div>
                    <div className="flex gap-4 text-xs font-mono">
                      <span className="text-[#60a5fa]/70">{fmtCountry(l.gross, l.country)}</span>
                      <span className="text-white/30">{l.headcount} staff</span>
                    </div>
                  </div>
                  <div className="flex-1 h-1.5 rounded-full bg-white/5">
                    <div className="h-full rounded-full bg-[#60a5fa]/50"
                      style={{ width: `${Math.round((l.gross / maxLocCost) * 100)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "trend" && (
            <div className="space-y-3">
              {trend.length === 0 ? (
                <p className="text-white/20 text-sm italic text-center py-6">No trend data.</p>
              ) : (
                <>
                  <div className="flex items-end gap-1 h-32">
                    {trend.map((t, i) => {
                      const max = Math.max(...trend.map(x => x.gross || 0));
                      const pct = max > 0 ? ((t.gross || 0) / max) * 100 : 0;
                      const netPct = max > 0 ? ((t.net || 0) / max) * 100 : 0;
                      return (
                        <div key={i} className="flex-1 flex flex-col items-center gap-0.5 group relative">
                          <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-black border border-white/10 rounded px-1.5 py-0.5 text-[10px] text-white/60 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                            {fmt(t.gross)}
                          </div>
                          <div className="w-full flex flex-col-reverse rounded-t-sm overflow-hidden" style={{ height: "100%" }}>
                            <div style={{ height: `${pct}%`, background: "#60a5fa30" }} />
                            <div style={{ height: `${netPct}%`, background: "#60a5fa80", position: "absolute", bottom: 0, width: "100%" }} />
                          </div>
                          <span className="text-[9px] text-white/20">{t.label?.slice(-3)}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex items-center gap-4 justify-center text-xs text-white/30">
                    <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-[#60a5fa30]"/>Gross</span>
                    <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-[#60a5fa80]"/>Net</span>
                  </div>
                </>
              )}
            </div>
          )}

          {tab === "exceptions" && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2 mb-4">
                {[
                  { label: "Unresolved", value: data.exceptions_open, color: "#f87171" },
                  { label: "Missing Bank", value: data.exceptions_bank, color: "#fbbf24" },
                  { label: "Negative Net", value: data.exceptions_negative_net, color: "#fb923c" },
                ].map(({ label, value, color }) => (
                  <div key={label} className="rounded-xl border border-white/5 bg-white/3 p-3 text-center">
                    <p className="text-xl font-bold font-mono" style={{ color }}>{value ?? 0}</p>
                    <p className="text-xs text-white/30 mt-0.5">{label}</p>
                  </div>
                ))}
              </div>
              {(data.exceptions_open > 0) ? (
                <div className="rounded-xl border border-red-500/20 bg-red-500/5 px-4 py-3">
                  <p className="text-sm text-red-400 font-medium">{data.exceptions_open} exceptions need resolution before payroll can be approved.</p>
                  <p className="text-xs text-white/30 mt-1">Go to Admin → Payroll → Exceptions to resolve.</p>
                </div>
              ) : (
                <div className="rounded-xl border border-[#00ff90]/20 bg-[#00ff90]/5 px-4 py-3">
                  <p className="text-sm text-[#00ff90]">✓ No unresolved payroll exceptions.</p>
                </div>
              )}
            </div>
          )}
          </>
        )}
      </div>
    </div>
  );
}